import { useEffect, useState } from "react";
import { getTigerResource } from "../../utils/tigerResource";
import { acrylicStyles, acrylicTokens } from "../../theme/acrylic";
import { isCdromPromptOpen, subscribeCdromPrompt } from "../../utils/cdromPrompt";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

interface TrendProgress {
  config: string;
  turbine: string;
  date: string;
  status: number;
  /** RWDate Julian-day bounds + the day being processed (for the % bar). */
  start_date: number;
  current_date: number;
  end_date: number;
}

interface DiagProgress {
  process_id: string;
  config_name: string;
  turbine: string;
  date: string;
  start_time: number;
  current_time: number;
  end_time: number;
}

/** A current operation. `pct` is the determinate bar value (0..100), or null
 *  to fall back to the indeterminate sweep when no percentage is available. */
type Op = { kind: "trend" | "diag"; turbine: string; date: string; pct: number | null };

/**
 * `TREND_STATE::TREND_COMPLETE` from the C++ `pc_common.h`. The trend server
 * streams `TREND_RUNNING` (2) while building and `TREND_COMPLETE` (4) when done;
 * `Ti_store_trend_progress` closes the dialog only on `TREND_COMPLETE` and
 * shows/updates for every other (in-progress) status. (0 is `TREND_NOT_SET`, NOT
 * "in progress" — gating on `status === 0` is why this dialog never appeared.)
 */
const TREND_COMPLETE = 4;

/** Diag progress percentage (offset by start_time), clamped to 0..100. */
function diagPercent(p: DiagProgress): number {
  if (p.end_time <= p.start_time) return 0;
  const v = ((p.current_time - p.start_time) / (p.end_time - p.start_time)) * 100;
  return Math.min(100, Math.max(0, v));
}

/**
 * Trend build percentage, mirroring the C++ `Ti_store_trend_progress`:
 * `((current − start) * 100) / (end + 1 − start)` over RWDate Julian days (the
 * `+1` keeps it short of 100% until TREND_COMPLETE closes the dialog). Returns
 * null when the bounds are missing/degenerate so the bar falls back to the
 * indeterminate sweep.
 */
function trendPercent(p: TrendProgress): number | null {
  const span = p.end_date + 1 - p.start_date;
  if (!Number.isFinite(span) || span <= 0) return null;
  const v = ((p.current_date - p.start_date) * 100) / span;
  if (!Number.isFinite(v)) return null;
  return Math.min(100, Math.max(0, v));
}

/**
 * Main-window progress for historical trend builds and historical diagnostics
 * queries — the build/processing counterpart to `DownloadProgressDialog`. Opens
 * on the first `trend-progress` / `diag:progress` event, closes on
 * `trend-finished` / `diag-finished`. Suppressed while an RDD download is in
 * flight so the diag->download->diag handoff never shows two cards, and while
 * the CD-ROM Data Request prompt is open so it can't bury the prompt (it
 * mounts after DataRequestDialog on the same z-index). Cancel issues
 * `cancel_active_trend` / `cancel_active_diag_query`; the dialog closes when the
 * resulting finished event arrives. Self-contained: mount once.
 */
export function HistoricalProgressDialog() {
  const [op, setOp] = useState<Op | null>(null);
  const [downloadActive, setDownloadActive] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cdromPrompt, setCdromPrompt] = useState(isCdromPromptOpen());

  useEffect(() => subscribeCdromPrompt(setCdromPrompt), []);

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: Array<() => void> = [];
    const track = (off: () => void) => {
      if (cancelled) off();
      else offs.push(off);
    };

    void import("@tauri-apps/api/event").then(async ({ listen }) => {
      track(
        await listen<TrendProgress>("trend-progress", (e) => {
          const p = e.payload;
          // Mirror C++ Ti_store_trend_progress: close on TREND_COMPLETE, else
          // show/update for any in-progress status (server streams TREND_RUNNING).
          if (p.status === TREND_COMPLETE) {
            setOp((cur) => (cur?.kind === "trend" ? null : cur));
          } else {
            setCancelling(false);
            setOp({
              kind: "trend",
              turbine: p.turbine || p.config,
              date: p.date,
              pct: trendPercent(p),
            });
          }
        }),
      );
      track(
        await listen("trend-finished", () => {
          setOp((cur) => (cur?.kind === "trend" ? null : cur));
          setCancelling(false);
        }),
      );
      track(
        await listen<DiagProgress>("diag:progress", (e) => {
          const p = e.payload;
          setCancelling(false);
          setOp({
            kind: "diag",
            turbine: p.turbine || p.config_name,
            date: p.date,
            pct: diagPercent(p),
          });
        }),
      );
      track(
        await listen("diag-finished", () => {
          setOp((cur) => (cur?.kind === "diag" ? null : cur));
          setCancelling(false);
        }),
      );
      track(await listen("download-progress", () => setDownloadActive(true)));
      track(await listen("download-finished", () => setDownloadActive(false)));
    });

    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  if (!op || downloadActive || cdromPrompt) return null;

  const title = getTigerResource(
    op.kind === "trend" ? "TIGER_HIST_TREND_PROGRESS_TITLE" : "TIGER_DIAG_PROGRESS_TITLE",
  );
  const label = `${getTigerResource("TIGER_PROCESSING")}${op.turbine} ${op.date}`;
  const pct = op.pct;

  const onCancel = async () => {
    setCancelling(true);
    if (!isTauri()) {
      setOp(null);
      return;
    }
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke(op.kind === "diag" ? "cancel_active_diag_query" : "cancel_active_trend");
    // The dialog closes when the matching *-finished event arrives.
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button
            className="acrylic-close-btn"
            onClick={onCancel}
            disabled={cancelling}
            title={getTigerResource("TIGER_RES_CANCEL")}
          >
            &#x2715;
          </button>
        </div>
        <div style={styles.body}>
          <div style={styles.label}>{label}</div>
          <div style={styles.barOuter}>
            {pct !== null ? (
              <>
                <div style={{ ...styles.barInner, width: `${pct}%` }} />
                <div style={styles.barLabel}>{pct.toFixed(0)}%</div>
              </>
            ) : (
              <div className="tiger-indeterminate-bar" />
            )}
          </div>
          <div style={styles.buttons}>
            <button className="btn-acrylic-secondary" onClick={onCancel} disabled={cancelling}>
              {cancelling ? getTigerResource("TIGER_DOWNLOAD_CANCELLING") : getTigerResource("TIGER_RES_CANCEL")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  ...acrylicStyles,
  overlay: {
    ...acrylicStyles.overlay,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialog: {
    background: "var(--ui-surface)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    border: "1px solid rgba(255,255,255,0.65)",
    borderRadius: 14,
    width: 420,
    maxWidth: "95vw",
    boxShadow: "0 16px 48px rgba(20,30,60,0.35)",
    overflow: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: acrylicTokens.font,
    color: acrylicTokens.textPrimary,
  },
  barOuter: {
    position: "relative" as const,
    height: 20,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(120,130,150,0.35)",
    borderRadius: 7,
    overflow: "hidden",
  },
  barInner: {
    height: "100%",
    background: "var(--ui-btn-primary-bg)",
    transition: "width 0.2s ease",
  },
  barLabel: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 600,
    color: "#4b5260",
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
} as const;
